import { Box, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { ChartNoAxesCombined, MoveHorizontal, RotateCcw, Scaling, Settings } from 'lucide-react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import handleDragIcon from '../assets/icons/handle_drag.svg';
import o2kButtonIcon from '../assets/icons/O2K_button_icon.png';
import { useAnalysisStore } from '../stores/analysisStore';
import { DATA_TYPE_META, resolveTraceColor, useGraphStore } from '../stores/graphStore';
import ChamberPlotView from './ChamberPlotView';
import { ButtonPush } from './ui/ButtonPush';
import HandleRightLarge from './ui/HandleLeftLarge';
import HandleRightSmall from './ui/HandleRightSmall';
import WindowBackground from './ui/WindowBackground';

// Per-chamber channels live on tab.chambers[idx].data; anything else is a
// chamberless device signal looked up on tab.deviceSeries by channel name.
const CHAMBER_CHANNELS = new Set(['O2', 'O2Flux']);

// Resolve a trace definition to {trace, time:Date[], value:number[]} or null
// if the data source isn't available. `sources` is {chambers, deviceSeries}
// so callers can memoize on those refs without depending on the full tab.
function resolveTraceData(trace, sources, dateCache) {
  if (!trace || !sources) return null;
  if (!CHAMBER_CHANNELS.has(trace.channel)) {
    const ds = sources.deviceSeries?.[trace.channel];
    if (!ds?.data || !ds?.time) return null;
    return {
      trace,
      time: getCachedDates(dateCache, `device:${trace.channel}`, ds.time),
      value: ds.data,
    };
  }
  // Per-chamber channels.
  const chamber = trace.chamberIndex != null ? sources.chambers?.[trace.chamberIndex] : null;
  const chData = chamber?.data;
  if (!chData?.timeSeries) return null;
  const value = trace.channel === 'O2' ? chData.oxygenSeries : chData.fluxSeries;
  if (!Array.isArray(value)) return null;
  const n = Math.min(chData.timeSeries.length, value.length);
  return {
    trace,
    time: getCachedDates(dateCache, `ch:${trace.chamberIndex}`, chData.timeSeries, n),
    value: n === value.length ? value : value.slice(0, n),
  };
}

// Cache one Date[] per cacheKey, keyed by the source array's identity. With
// Zustand-stable references the source array stays === across re-resolves,
// so we avoid re-allocating millions of Date objects per chamber switch.
function getCachedDates(cache, cacheKey, timeSeries, length = timeSeries.length) {
  const entry = cache.get(cacheKey);
  if (entry && entry.source === timeSeries && entry.dates.length === length) {
    return entry.dates;
  }
  const dates = new Array(length);
  for (let i = 0; i < length; i++) dates[i] = new Date(timeSeries[i] * 1000);
  cache.set(cacheKey, { source: timeSeries, dates });
  return dates;
}

const ChamberGraph = ({
  tabId,
  graphId,
  isActive = false,
  onActivate,
  onOpenO2K,
  onOpenSettings,
}) => {
  const graphControlsRef = useRef({});
  const [cursor, setCursor] = useState(null);

  // Narrow selectors: chambers/deviceSeries refs stay === across
  // setActiveChamber so traceData below doesn't re-memoize on activation.
  const chambers = useAnalysisStore((state) => state.openFiles[tabId]?.chambers);
  const deviceSeries = useAnalysisStore((state) => state.openFiles[tabId]?.deviceSeries);
  const graph = useGraphStore((state) => state.tabGraphs[tabId]?.find((g) => g.id === graphId));
  // Chamber graphs bind to a chamber for side-label + loading guard. Marks/
  // events follow the SELECTED trace's source chamber (computed below).
  const chamberIndex = graph?.kind === 'chamber' ? graph.chamberIndex : null;
  const chamber = chamberIndex != null ? chambers?.[chamberIndex] : null;
  const setSelectedTrace = useGraphStore((state) => state.setSelectedTrace);

  const selectedTrace = useMemo(() => {
    if (!graph?.traces?.length) return null;
    return graph.traces.find((t) => t.id === graph.selectedTraceId) ?? graph.traces[0];
  }, [graph?.traces, graph?.selectedTraceId]);
  const sourceChamberIndex = selectedTrace?.chamberIndex ?? chamberIndex;
  const sourceChamber = sourceChamberIndex != null ? chambers?.[sourceChamberIndex] : null;

  const dateCacheRef = useRef(new Map());

  const traceData = useMemo(() => {
    if (!graph) return [];
    const sources = { chambers, deviceSeries };
    return (graph.traces || [])
      .map((t) => resolveTraceData(t, sources, dateCacheRef.current))
      .filter((td) => td !== null);
  }, [graph, chambers, deviceSeries]);

  // Time extent passed to empty graphs so they share the chambers'
  // baseXScale.domain — without this, navigationStore xTransforms produce
  // a different visible window on graphs with no traces.
  const referenceTimeExtentMs = useMemo(() => {
    if (!chambers) return null;
    let bestStart = Infinity;
    let bestEnd = -Infinity;
    for (const ch of Object.values(chambers)) {
      const ts = ch?.data?.timeSeries;
      if (!Array.isArray(ts) || ts.length < 2) continue;
      const start = ts[0] * 1000;
      const end = ts[ts.length - 1] * 1000;
      if (start < bestStart) bestStart = start;
      if (end > bestEnd) bestEnd = end;
    }
    if (!Number.isFinite(bestStart) || !Number.isFinite(bestEnd)) return null;
    return [bestStart, bestEnd];
  }, [chambers]);

  // Side handle label / radio row prefix. Blank for extras — they aren't
  // bound to a specific chamber.
  const chamberLabel = chamberIndex != null ? String(chamberIndex) : '';

  // Format the cursor x value in the graph's configured time unit.
  const formatCursorTime = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    const unit = graph?.scaling?.xUnit ?? 'hh:mm:ss';
    const totalSec = Math.max(0, Math.floor(date.getTime() / 1000));
    if (unit === 'seconds') return String(totalSec);
    const hh = String(Math.floor(totalSec / 3600)).padStart(2, '0');
    const mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
    const ss = String(totalSec % 60).padStart(2, '0');
    return unit === 'hh:mm' ? `${hh}:${mm}` : `${hh}:${mm}:${ss}`;
  };
  const formatCursorValue = (v) =>
    typeof v === 'number' && Number.isFinite(v) ? v.toFixed(2) : '';

  // F6 requests the shared dialog (hosted by ChamberGrid) for this graph.
  useHotkeys(
    'f6',
    (ev) => {
      ev.preventDefault();
      if (!isActive) return;
      onOpenSettings?.(graphId);
    },
    { enableOnFormTags: false },
    [isActive, onOpenSettings, graphId],
  );

  const handleOpenSettings = useCallback(() => {
    if (!isActive) onActivate?.();
    onOpenSettings?.(graphId);
  }, [isActive, onActivate, onOpenSettings, graphId]);

  const handleTraceSelect = (event) => {
    if (!graph) return;
    setSelectedTrace(tabId, graph.id, event.target.value);
    if (!isActive) onActivate?.();
  };

  const handleClick = () => {
    if (!isActive) onActivate?.();
  };

  const traceLabel = (trace) => {
    const meta = DATA_TYPE_META[trace.channel];
    return meta?.label ?? trace.channel;
  };

  const selectedTraceId = graph?.selectedTraceId ?? graph?.traces?.[0]?.id ?? '';

  return (
    <Box
      onClick={handleClick}
      sx={{
        height: '100%',
        display: 'flex',
        gap: '2px',
        boxShadow:
          '0 2px 4px rgba(125, 141, 151, 0.17), 0 7px 7px rgba(125, 141, 151, 0.14), 0 15px 9px rgba(125, 141, 151, 0.09), 0 26px 11px rgba(125, 141, 151, 0.03), 0 41px 12px rgba(125, 141, 151, 0.00)',
        borderRadius: '16px',
        cursor: !isActive ? 'pointer' : 'default',
      }}
    >
      <HandleRightLarge label={chamberLabel} height="100%" isActive={isActive} />
      <WindowBackground isActive={isActive}>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <ChamberPlotView
            controlsRef={graphControlsRef}
            chamber={chamberLabel}
            height="100%"
            isActive={isActive}
            tabId={tabId}
            traceData={traceData}
            marks={sourceChamber?.marks || []}
            events={sourceChamber?.events || []}
            graph={graph || null}
            referenceTimeExtentMs={referenceTimeExtentMs}
            isLoading={chamberIndex != null && !chamber}
            error={null}
            onCursorChange={setCursor}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            marginInline: '20px',
            marginBlock: '28px',
            flex: '0 0 auto',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 220 }}>
            <RadioGroup
              value={isActive ? selectedTraceId : ''}
              onChange={handleTraceSelect}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              {(graph?.traces ?? []).map((trace) => (
                <FormControlLabel
                  key={trace.id}
                  value={trace.id}
                  control={
                    <Radio
                      sx={{
                        color: resolveTraceColor(trace.color),
                        '&.Mui-checked': { color: resolveTraceColor(trace.color) },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ userSelect: 'none' }}>
                      {chamberLabel ? `${chamberLabel}: ` : ''}
                      {traceLabel(trace)}
                    </Typography>
                  }
                  sx={{ height: '24px' }}
                />
              ))}
            </RadioGroup>

            {isActive && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  columnGap: '12px',
                  rowGap: '4px',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  color: '#394b57',
                  minHeight: '60px',
                  userSelect: 'none',
                }}
              >
                <Typography sx={{ fontFamily: 'inherit', fontSize: 'inherit' }}>x:</Typography>
                <Typography sx={{ fontFamily: 'inherit', fontSize: 'inherit', textAlign: 'right' }}>
                  {cursor ? formatCursorTime(cursor.time) : ''}
                </Typography>
                <Typography sx={{ fontFamily: 'inherit', fontSize: 'inherit' }}>y1:</Typography>
                <Typography sx={{ fontFamily: 'inherit', fontSize: 'inherit', textAlign: 'right' }}>
                  {cursor ? formatCursorValue(cursor.y1) : ''}
                </Typography>
                <Typography sx={{ fontFamily: 'inherit', fontSize: 'inherit' }}>y2:</Typography>
                <Typography sx={{ fontFamily: 'inherit', fontSize: 'inherit', textAlign: 'right' }}>
                  {cursor ? formatCursorValue(cursor.y2) : ''}
                </Typography>
              </Box>
            )}
          </Box>

          {isActive && (
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                marginTop: 'auto',
              }}
            >
              <ButtonPush
                borderColor="transparent"
                tooltipText="Autoscale time axis"
                onClick={() => graphControlsRef.current.resetXAxis?.()}
              >
                <MoveHorizontal strokeWidth={1.5} size={18} />
              </ButtonPush>
              <ButtonPush
                borderColor="transparent"
                tooltipText="Reset Zoom"
                onClick={() => graphControlsRef.current.resetZoom?.()}
              >
                <Scaling strokeWidth={1.5} size={20} />
              </ButtonPush>
              <ButtonPush borderColor="transparent" tooltipText="Layout presets" disabled>
                <RotateCcw strokeWidth={1.5} size={18} />
              </ButtonPush>
              <ButtonPush
                borderColor="transparent"
                tooltipText="Graph options (F6)"
                onClick={handleOpenSettings}
              >
                <Settings strokeWidth={1.5} size={18} />
              </ButtonPush>
              <ButtonPush borderColor="transparent" tooltipText="Combined chart" disabled>
                <ChartNoAxesCombined strokeWidth={1.5} size={18} />
              </ButtonPush>
              {onOpenO2K && (
                <ButtonPush
                  tooltipText="O2 Kinetics"
                  borderColor="transparent"
                  onClick={() => onOpenO2K?.(chamberIndex)}
                >
                  <img src={o2kButtonIcon} width={26} alt="O2K Button" />
                </ButtonPush>
              )}
            </Stack>
          )}
        </Box>
      </WindowBackground>
      <HandleRightSmall height="100%" isActive={isActive}>
        <img src={handleDragIcon} alt="Drag Handle" />
      </HandleRightSmall>
    </Box>
  );
};

export default ChamberGraph;
